/**
 * Swagger Parameter Utilities
 *
 * Handles extraction and serialization of parameters for swagger-based operations
 * (ApiConnection type). These operations store parameters in a specific format:
 * - Path parameters are embedded in the `inputs.path` template string
 * - Query parameters are stored in `inputs.queries` object
 * - Header parameters are stored in `inputs.headers` object
 * - Body parameters are stored in `inputs.body` object
 *
 * This module provides utilities to:
 * - Extract parameter values from the wire format to UI-friendly format
 * - Serialize parameter values back to the wire format for saving
 *
 * Note: For extraction, we work without requiring the swagger path template
 * by extracting queries directly and preserving the path as-is for now.
 * Full path parameter extraction requires the template which comes from swagger.
 */

import type { ParameterInfo, ParameterLocation } from '@/types/operationParameter';
import type { ValueSegment } from '@/types/valueSegment';
import { parseValueToSegments, serializeSegmentsToValue } from '@/services/tokenParsingService';
import { serializeHost, applyHostSerialization } from '@/services/hostSerializer';
import { serializeParameterValue } from '@/services/workflowSerializer';
import { isObject } from '@/utils/primitives';
import type { ParameterValue } from './parameterUtils';
import { isManagedApiConnector, toShortManagedApiId } from '@/utils/connections';

// =============================================================================
// SHARED HELPERS
// =============================================================================

/**
 * A parameter value is considered "unset" when it's undefined, null, or an
 * empty string. Used everywhere we decide whether to serialize a field or
 * fall back to a swagger default. Written as a negative type guard so the
 * `if (!isEmptyParamValue(v))` branch narrows out `undefined | null | ''`.
 */
function isEmptyParamValue<T>(value: T): value is Extract<T, undefined | null | ''> {
  return value === undefined || value === null || value === '';
}

/**
 * Decode an extracted path segment, treating an unresolved `{name}` placeholder
 * as empty. Newly created nodes contain literal placeholder text (e.g.
 * `{poster}`) for unfilled segments; we don't want those to round-trip as
 * `${encodeURIComponent('{poster}')}` on save or render in the editor.
 */
function decodePathParameterOrPlaceholder(name: string, encodedValue: string): string {
  const decoded = decodePathParameterValue(encodedValue);
  return decoded === `{${name}}` ? '' : decoded;
}

// =============================================================================
// PATH PARAMETER EXTRACTION
// =============================================================================

/**
 * Drop the leading `/{connectionId}` segment from a swagger path template.
 * Managed-connector swaggers declare it, but saved actions never persist it,
 * so it must be removed before any template/path round-trip (extraction,
 * substitution, regex matching). No-op if absent.
 */
export function stripConnectionIdPrefix(pathTemplate: string): string {
  const prefix = '/{connectionId}';
  return pathTemplate.startsWith(prefix) ? pathTemplate.slice(prefix.length) : pathTemplate;
}

/**
 * Extract parameter values from a path string using a path template.
 *
 * Example:
 * - pathTemplate: "/v2/datasets/{dataset}/triggers/batch/{operation}"
 * - pathValue: "/v2/datasets/@{encodeURIComponent(encodeURIComponent('AccountName'))}/triggers/batch/onupdatedfile"
 * - Result: { dataset: "@{encodeURIComponent(encodeURIComponent('AccountName'))}", operation: "onupdatedfile" }
 *
 * @param pathValue - The actual path value with parameter values embedded
 * @param pathTemplate - The swagger path template with {paramName} placeholders
 * @returns Record of parameter names to their extracted values
 */
export function extractPathParameters(
  pathValue: string | undefined,
  pathTemplate: string | undefined
): Record<string, string> {
  if (!pathValue || !pathTemplate) return {};

  const result: Record<string, string> = {};

  // Find all parameter placeholders in the template: {paramName}
  const paramRegex = /\{([^}]+)\}/g;
  const paramNames: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = paramRegex.exec(pathTemplate)) !== null) {
    if (match[1]) {
      paramNames.push(match[1]);
    }
  }

  if (paramNames.length === 0) return {};

  // Strategy: Split both template and value by '/' and match segment by segment
  const templateSegments = pathTemplate.split('/');
  const valueSegments = pathValue.split('/');

  if (templateSegments.length !== valueSegments.length) {
    // Mismatch in segment count - try fuzzy matching
    return decodeExtractedPathParams(extractPathParametersFuzzy(pathValue, pathTemplate));
  }

  for (let i = 0; i < templateSegments.length; i++) {
    const templateSeg = templateSegments[i];
    const valueSeg = valueSegments[i];

    // Skip if segments are undefined
    if (!templateSeg || !valueSeg) continue;

    // Find parameters in this segment
    const segParamRegex = /\{([^}]+)\}/g;
    let segMatch: RegExpExecArray | null;
    const segParams: string[] = [];
    while ((segMatch = segParamRegex.exec(templateSeg)) !== null) {
      if (segMatch[1]) {
        segParams.push(segMatch[1]);
      }
    }

    if (segParams.length === 0) {
      // No parameters in this segment, skip
      continue;
    }

    if (segParams.length === 1) {
      // Single parameter in segment - extract its value
      const paramName = segParams[0];
      if (!paramName) continue;

      const prefix = templateSeg.substring(0, templateSeg.indexOf('{'));
      const suffix = templateSeg.substring(templateSeg.lastIndexOf('}') + 1);

      let value = valueSeg;
      if (prefix && value.startsWith(prefix)) {
        value = value.substring(prefix.length);
      }
      if (suffix && value.endsWith(suffix)) {
        value = value.substring(0, value.length - suffix.length);
      }

      result[paramName] = value;
    } else {
      // Multiple parameters in segment - this is complex
      // For now, try to extract using the expression pattern
      const extracted = extractMultipleParamsFromSegment(templateSeg, valueSeg, segParams);
      Object.assign(result, extracted);
    }
  }

  return decodeExtractedPathParams(result);
}

/** Decode every extracted path-param value, mapping unresolved `{name}` placeholders to ''. */
function decodeExtractedPathParams(raw: Record<string, string>): Record<string, string> {
  const decoded: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    decoded[key] = decodePathParameterOrPlaceholder(key, value);
  }
  return decoded;
}

/**
 * Decode a path parameter value from its expression form to the literal value.
 *
 * Only `encodeURIComponent('literal')` wrappers (any depth) and bare
 * `'literal'` expressions are decoded. Arbitrary workflow expressions
 * (other function calls, property access, etc.) are preserved verbatim so
 * dynamic content dropped onto a path parameter round-trips through
 * save/reload — without this guard, an expression like
 * `@{triggerBody()?['pullNumber']}` would be "decoded" to the literal
 * string `pullNumber` (the bracket-property name) and the token would
 * vanish on the next render.
 *
 * Examples:
 * - `@{encodeURIComponent('Flow bot')}` → `Flow bot`
 * - `@{encodeURIComponent(encodeURIComponent('value'))}` → `value`
 * - `@{'value'}` → `value`
 * - `literal value` → `literal value` (unchanged)
 * - `@{triggerBody()?['pullNumber']}` → unchanged (dynamic expression)
 * - `@{int(triggerBody()?.pullNumber)}` → unchanged (dynamic expression)
 */
function decodePathParameterValue(value: string): string {
  if (!value) return value;

  const expressionMatch = value.match(/^[@$]\{(.+)\}$/s);
  if (!expressionMatch) return value;

  const innerExpression = expressionMatch[1];
  if (!innerExpression) return value;

  // Peel any number of outer `encodeURIComponent(...)` wrappers. Anchoring to
  // the closing `)` of the whole expression prevents over-matching past
  // balanced calls like `concat(encodeURIComponent('a'), 'b')`.
  let inner = innerExpression.trim();
  while (true) {
    const enc = inner.match(/^encodeURIComponent\((.+)\)$/s);
    if (!enc || !enc[1]) break;
    inner = enc[1].trim();
  }

  // After unwrapping, the remainder must be a single `'literal'` (doubled
  // single quotes allowed) for decoding to be safe. Anything else stays as
  // the original `@{...}` expression.
  const literalMatch = inner.match(/^'((?:[^']|'')*)'$/s);
  if (literalMatch && literalMatch[1] !== undefined) {
    return literalMatch[1].replace(/''/g, "'");
  }

  return value;
}

// =============================================================================
// METADATA-DRIVEN PATH PARAMETER EXTRACTION
// =============================================================================

/**
 * Extract path parameters using metadata from parameter definitions.
 *
 * This function uses ParameterInfo.info.serialization metadata when available
 * to enable manifest-driven extraction. Falls back to regex-based extraction
 * when metadata is not present.
 *
 * Benefits of metadata-driven extraction:
 * - Uses canonical parameter names from swagger
 * - Leverages serialization.property.type to understand parameter format
 * - Works consistently for all connectors without special-casing
 *
 * @param pathValue - The actual path value with parameter values embedded
 * @param pathTemplate - The swagger path template with {paramName} placeholders
 * @param parameters - Optional array of ParameterInfo with serialization metadata
 * @returns Record of parameter names to their extracted values
 */
export function extractPathParametersWithMetadata(
  pathValue: string | undefined,
  pathTemplate: string | undefined,
  parameters?: ParameterInfo[]
): Record<string, string> {
  if (!pathValue || !pathTemplate) return {};

  // If we have parameter definitions, try to use them for canonical naming
  if (parameters && parameters.length > 0) {
    // Find path parameters (those with in: 'path')
    const pathParams = parameters.filter((p) => p.info?.in === 'path');

    if (pathParams.length > 0) {
      // Extract using standard method
      const extracted = extractPathParameters(pathValue, pathTemplate);

      // Map to canonical names from ParameterInfo
      const result: Record<string, string> = {};
      for (const [extractedName, value] of Object.entries(extracted)) {
        // Find the matching ParameterInfo by name (case-insensitive)
        const param = pathParams.find(
          (p) => p.parameterName.toLowerCase() === extractedName.toLowerCase()
        );

        // Use canonical name from ParameterInfo if found, otherwise use extracted name
        const canonicalName = param?.parameterName || extractedName;
        result[canonicalName] = value;

      }

      return result;
    }
  }

  // Fallback to standard regex-based extraction
  return extractPathParameters(pathValue, pathTemplate);
}

/**
 * Fuzzy matching for path parameters when segment counts don't match — typically
 * because a substituted expression value contains '/' characters (e.g. a
 * SharePoint dataset URL like `https://contoso.sharepoint.com/teams/Foo`).
 *
 * Strategy: split the template into alternating literal / placeholder tokens,
 * then anchor each literal in the runtime value via `indexOf`. The text
 * between adjacent literal anchors is the value of the placeholder between
 * them. This ignores whatever characters prior placeholders' substituted text
 * may contain (`/`, `}`, `)`, quotes from double URL-encoding, etc.).
 */
function extractPathParametersFuzzy(pathValue: string, pathTemplate: string): Record<string, string> {
  // String.split with a capture group yields [literal, name, literal, name, ..., literal].
  // Even indices are literal text; odd indices are placeholder names.
  const tokens = pathTemplate.split(/\{([^}]+)\}/);
  const result: Record<string, string> = {};
  let cursor = 0;
  let pendingPlaceholder: string | undefined;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!;
    if (i % 2 === 1) {
      // Placeholder name — its value is bounded by the next literal.
      pendingPlaceholder = token;
      continue;
    }
    if (!token) continue;
    const idx = pathValue.indexOf(token, cursor);
    if (idx === -1) {
      // Literal anchor missing — return whatever we extracted so far.
      return result;
    }
    if (pendingPlaceholder) {
      const value = pathValue.substring(cursor, idx);
      if (value) result[pendingPlaceholder] = value;
      pendingPlaceholder = undefined;
    }
    cursor = idx + token.length;
  }

  // Trailing placeholder with no terminating literal: take the rest of the value.
  if (pendingPlaceholder) {
    const tail = pathValue.substring(cursor);
    if (tail) result[pendingPlaceholder] = tail;
  }

  return result;
}

/**
 * Extract multiple parameters from a single path segment.
 * Example: "{param1}-{param2}" from "value1-value2"
 */
function extractMultipleParamsFromSegment(
  templateSeg: string,
  valueSeg: string,
  paramNames: string[]
): Record<string, string> {
  const result: Record<string, string> = {};

  // Build a regex from the template segment
  const pattern = templateSeg
    .replace(/[.*+?^$|[\]\\]/g, '\\$&')
    .replace(/\{[^}]+\}/g, '(.+?)');

  try {
    const regex = new RegExp(`^${pattern}$`);
    const match = valueSeg.match(regex);
    if (match) {
      for (let i = 0; i < paramNames.length; i++) {
        const paramName = paramNames[i];
        if (paramName) {
          result[paramName] = match[i + 1] || '';
        }
      }
    }
  } catch {
    // Regex failed, return empty
  }

  return result;
}

// =============================================================================
// QUERY PARAMETER EXTRACTION
// =============================================================================

/**
 * Extract query parameter values from the queries object.
 *
 * @param queries - The queries object from inputs.queries
 * @returns Record of parameter names to their values
 */
export function extractQueryParameters(
  queries: Record<string, unknown> | undefined
): Record<string, ParameterValue> {
  if (!queries) return {};

  const result: Record<string, ParameterValue> = {};

  for (const [key, value] of Object.entries(queries)) {
    // Parse expressions in string values
    if (typeof value === 'string' && (value.includes('@') || value.includes('${'))) {
      const parseResult = parseValueToSegments(value);
      if (parseResult.success && parseResult.segments.length > 0) {
        result[key] = parseResult.segments;
      } else {
        result[key] = value;
      }
    } else {
      result[key] = value as ParameterValue;
    }
  }

  return result;
}

// =============================================================================
// HEADER PARAMETER EXTRACTION
// =============================================================================

/**
 * Extract header parameter values from the headers object.
 *
 * @param headers - The headers object from inputs.headers
 * @returns Record of parameter names to their values
 */
export function extractHeaderParameters(
  headers: Record<string, unknown> | undefined
): Record<string, ParameterValue> {
  if (!headers) return {};

  const result: Record<string, ParameterValue> = {};

  for (const [key, value] of Object.entries(headers)) {
    if (typeof value === 'string' && (value.includes('@') || value.includes('${'))) {
      const parseResult = parseValueToSegments(value);
      if (parseResult.success && parseResult.segments.length > 0) {
        result[key] = parseResult.segments;
      } else {
        result[key] = value;
      }
    } else {
      result[key] = value as ParameterValue;
    }
  }

  return result;
}

// =============================================================================
// BODY PARAMETER EXTRACTION
// =============================================================================

/**
 * Extract body parameter values with deep flattening support.
 *
 * Creates multiple key formats for compatibility with different parameter resolution patterns:
 * - `body.recipient` = object (for legacy patterns)
 * - `recipient.groupId` = value (for dynamic schema parameters)
 * - `body.messageBody` = value (for legacy patterns)
 * - `messageBody` = value (for dynamic schema parameters)
 *
 * This ensures that dynamic schema parameters (which expect keys like `recipient.groupId`)
 * can find their values, while also maintaining backwards compatibility.
 *
 * @param body - The body from inputs.body
 * @returns Record of parameter names to their values
 */
export function extractBodyParameters(
  body: unknown
): Record<string, ParameterValue> {
  if (body === undefined || body === null) return {};

  // If body is a string expression, parse it
  if (typeof body === 'string') {
    if (body.includes('@') || body.includes('${')) {
      const parseResult = parseValueToSegments(body);
      if (parseResult.success && parseResult.segments.length > 0) {
        return { body: parseResult.segments };
      }
    }
    return { body };
  }

  // If body is a plain object, deeply flatten it
  if (isObject(body)) {
    const result: Record<string, ParameterValue> = {};

    // Helper to recursively flatten nested objects
    const flattenObject = (obj: Record<string, unknown>, bodyPrefix: string, noBodyPrefix: string) => {
      for (const [key, value] of Object.entries(obj)) {
        const bodyPrefixedKey = bodyPrefix ? `${bodyPrefix}.${key}` : `body.${key}`;
        const noPrefixKey = noBodyPrefix ? `${noBodyPrefix}.${key}` : key;

        if (value === null || value === undefined) {
          continue;
        } else if (typeof value === 'string') {
          // Handle string values (may contain expressions)
          if (value.includes('@') || value.includes('${')) {
            const parseResult = parseValueToSegments(value);
            if (parseResult.success && parseResult.segments.length > 0) {
              result[bodyPrefixedKey] = parseResult.segments;
              result[noPrefixKey] = parseResult.segments;
            } else {
              result[bodyPrefixedKey] = value;
              result[noPrefixKey] = value;
            }
          } else {
            result[bodyPrefixedKey] = value;
            result[noPrefixKey] = value;
          }
        } else if (isObject(value)) {
          // Nested object - store the object AND recursively flatten
          result[bodyPrefixedKey] = value as ParameterValue;
          result[noPrefixKey] = value as ParameterValue;
          flattenObject(value, bodyPrefixedKey, noPrefixKey);
        } else {
          // Primitive (number, boolean) or array - store as-is
          result[bodyPrefixedKey] = value as ParameterValue;
          result[noPrefixKey] = value as ParameterValue;
        }
      }
    };

    flattenObject(body, '', '');
    return result;
  }

  // Array or primitive
  return { body: body as ParameterValue };
}

// =============================================================================
// PARAMETER SERIALIZATION (for saving)
// =============================================================================

/**
 * Serialize path parameters back into a path string.
 *
 * @param params - Parameter values keyed by parameter name
 * @param pathTemplate - The swagger path template with {paramName} placeholders
 * @param encodeComponents - Whether to wrap values in encodeURIComponent
 * @returns The constructed path string
 */
export function serializePathParameters(
  params: Record<string, string>,
  pathTemplate: string,
  encodeComponents = true
): string {
  let path = pathTemplate;

  for (const [name, value] of Object.entries(params)) {
    const placeholder = `{${name}}`;
    if (path.includes(placeholder)) {
      // Keep the original `{name}` placeholder when unset rather than encoding
      // into `${encodeURIComponent('')}`.
      if (isEmptyParamValue(value)) continue;
      // If the value is already an expression or encoded, use as-is
      // Otherwise, wrap in encodeURIComponent for safety
      let finalValue = value;
      if (encodeComponents && !value.includes('@{') && !value.includes('${') && !value.includes('%')) {
        // Escape single quotes to prevent expression syntax breakage
        const escaped = value.replace(/'/g, "''");
        finalValue = `\${encodeURIComponent(encodeURIComponent('${escaped}'))}`;
      }
      path = path.replace(placeholder, finalValue);
    }
  }

  return path;
}

/**
 * Serialize query parameters back into a queries object.
 *
 * @param params - Parameter values keyed by parameter name
 * @param queryParamNames - Names of parameters that should go in queries
 * @returns The queries object
 */
export function serializeQueryParameters(
  params: Record<string, unknown>,
  queryParamNames: string[]
): Record<string, unknown> {
  const queries: Record<string, unknown> = {};

  for (const name of queryParamNames) {
    if (name in params && params[name] !== undefined && params[name] !== '') {
      queries[name] = params[name];
    }
  }

  return queries;
}

/**
 * Serialize header parameters back into a headers object.
 *
 * Extraction stores headers under the prefixed key `header.<name>` (to avoid
 * colliding with query/body keys); the editor writes user edits under the
 * bare `<name>` via `setParameter`. The bare key is authoritative: if the
 * user has touched the field (`name in params`) we use that value, including
 * empty/null/undefined values that clear the header. Otherwise we fall back
 * to the extracted prefixed value so unedited headers round-trip unchanged.
 *
 * @param params - Parameter values keyed by parameter name
 * @param headerParamNames - Names of parameters that should go in headers
 * @returns The headers object
 */
export function serializeHeaderParameters(
  params: Record<string, unknown>,
  headerParamNames: string[]
): Record<string, unknown> {
  const headers: Record<string, unknown> = {};

  for (const name of headerParamNames) {
    const value = name in params ? params[name] : params[`header.${name}`];
    if (!isEmptyParamValue(value)) {
      headers[name] = value;
    }
  }

  return headers;
}

/**
 * Serialize body parameters back into a body object.
 *
 * @param params - Parameter values, potentially with 'body.' prefix
 * @returns The body value (could be object, string, etc.)
 */
export function serializeBodyParameters(
  params: Record<string, unknown>
): unknown {
  // Helper to serialize ValueSegment arrays to strings
  const serializeValue = (value: unknown): unknown => {
    if (Array.isArray(value) && value.length > 0 && value[0]?.id && value[0]?.type) {
      return serializeSegmentsToValue(value);
    }
    return value;
  };

  // Check for simple 'body' key (non-object value like string expression)
  if ('body' in params) {
    const bodyValue = params.body;
    // Only use simple body if it's a string/primitive, not an object
    if (typeof bodyValue === 'string' || typeof bodyValue === 'number' || typeof bodyValue === 'boolean') {
      return bodyValue;
    }
    // If body is an array (ValueSegment[]), serialize it
    if (Array.isArray(bodyValue) && bodyValue.length > 0 && bodyValue[0]?.id && bodyValue[0]?.type) {
      return serializeValue(bodyValue);
    }
  }

  // Check for nested body.* values and reconstruct nested structure
  const bodyResult: Record<string, unknown> = {};
  let hasBodyParams = false;

  // Helper to set a nested value using dot notation
  const setNestedValue = (obj: Record<string, unknown>, path: string, value: unknown) => {
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]!;
      if (!(part in current) || typeof current[part] !== 'object' || current[part] === null) {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }
    const lastPart = parts[parts.length - 1]!;
    current[lastPart] = value;
  };

  for (const [key, value] of Object.entries(params)) {
    if (key.startsWith('body.')) {
      const bodyKey = key.substring(5); // Remove 'body.' prefix

      // Skip internal metadata keys
      if (bodyKey.startsWith('_')) continue;

      // Skip if value is an intermediate object (not a leaf value)
      if (isObject(value)) {
        if (!('id' in value && 'type' in value && 'value' in value)) {
          continue;
        }
      }

      const finalValue = serializeValue(value);
      setNestedValue(bodyResult, bodyKey, finalValue);
      hasBodyParams = true;
    }
  }

  return hasBodyParams ? bodyResult : undefined;
}

// =============================================================================
// DETECTION UTILITIES
// =============================================================================

/**
 * Action types that use swagger-based parameter format.
 * These operations decompose inputs into path/method/queries/headers/body.
 * Plain HTTP/HttpWebhook use uri-based format — NOT swagger.
 *
 * Matches LogicAppsUX's approach: discriminate by operation type, not input shape.
 * See: libs/designer/src/lib/core/utils/swagger/operation.ts:402-416
 */
const SWAGGER_BASED_TYPES = new Set([
  'ApiConnection',
  'ApiConnectionWebhook',
  'ApiConnectionNotification',
]);

/**
 * Check if an operation uses swagger-based parameter format.
 * Uses the operation's type field to discriminate, not input shape heuristics.
 * This matches LogicAppsUX's approach where ApiConnection-family types
 * use path/queries/headers decomposition, while Http types use uri-based format.
 *
 * @param inputs - The inputs object from the action/trigger
 * @param operationType - The action/trigger type (e.g., 'ApiConnection', 'Http')
 * @returns True if this is a swagger-based operation
 */
export function isSwaggerBasedOperation(inputs: unknown, operationType?: string): inputs is Record<string, unknown> {
  if (!isObject(inputs)) {
    return false;
  }

  // Primary: use operation type if available (principled approach)
  if (operationType) {
    return SWAGGER_BASED_TYPES.has(operationType);
  }

  // Fallback: structural detection for cases where type isn't available.
  // path/pathTemplate are unique to swagger operations; HTTP uses uri.
  return 'path' in inputs || 'pathTemplate' in inputs;
}

/**
 * Check if inputs are for an ApiConnectionNotification trigger.
 * These have fetch and subscribe objects instead of direct path/queries.
 */
export function isNotificationTriggerInputs(inputs: unknown): inputs is {
  fetch: { queries?: Record<string, unknown>; pathTemplate?: { template: string } };
  subscribe: { queries?: Record<string, unknown>; pathTemplate?: { template: string }; body?: Record<string, unknown> };
  host?: { connection?: { referenceName?: string } };
} {
  if (!isObject(inputs)) {
    return false;
  }

  return 'fetch' in inputs && 'subscribe' in inputs &&
    isObject((inputs as Record<string, unknown>).fetch) &&
    isObject((inputs as Record<string, unknown>).subscribe);
}

/**
 * Extract parameters from an ApiConnectionNotification trigger.
 * Notification triggers store query parameters in fetch.queries and subscribe.queries.
 *
 * Parameters are extracted with prefixed keys matching the swagger parameter format:
 * - fetch.queries.{paramName} -> for query params from fetch operation
 * - subscribe.queries.{paramName} -> for query params from subscribe operation (if different)
 *
 * Note: operationId is NOT stored in the workflow definition, following Logic Apps UX pattern.
 * It's resolved dynamically from the swagger using the fetch path template.
 *
 * @param inputs - The notification trigger inputs object
 * @returns Record of parameter values with prefixed keys
 */
export function extractNotificationTriggerParameters(
  inputs: {
    fetch: { queries?: Record<string, unknown>; pathTemplate?: { template: string }; method?: string };
    subscribe: { queries?: Record<string, unknown>; pathTemplate?: { template: string }; body?: Record<string, unknown>; method?: string };
    host?: { connection?: { referenceName?: string }; apiId?: string; operationId?: string };
  }
): Record<string, ParameterValue> {
  const values: Record<string, ParameterValue> = {};

  // Mark as notification trigger for serialization
  values['_isNotificationTrigger'] = true;

  // Extract connection info from host
  // Note: apiId is resolved from connectionReferences, not stored here
  if (inputs.host?.connection?.referenceName) {
    values['_connection'] = inputs.host.connection.referenceName;
  }
  if (inputs.host?.apiId) {
    values['_apiId'] = isManagedApiConnector(inputs.host.apiId) ? toShortManagedApiId(inputs.host.apiId) : inputs.host.apiId;
  }

  // Note: operationId is needed here to fetch node settings correctly.
  if (inputs.host?.operationId) {
    values['_operationId'] = inputs.host.operationId;
  }

  // Store fetch metadata for serialization
  if (inputs.fetch?.pathTemplate?.template) {
    values['_fetchPathTemplate'] = inputs.fetch.pathTemplate.template;
  }
  if (inputs.fetch?.method) {
    values['_fetchMethod'] = inputs.fetch.method;
  }

  // Store subscribe metadata for serialization
  if (inputs.subscribe?.pathTemplate?.template) {
    values['_subscribePathTemplate'] = inputs.subscribe.pathTemplate.template;
  }
  if (inputs.subscribe?.method) {
    values['_subscribeMethod'] = inputs.subscribe.method;
  }

  // Extract query parameters from fetch.queries with prefixed keys
  // Keys use format: fetch.queries.{paramName} to match swagger parameter keys
  const fetchQueryParamNames: string[] = [];
  if (inputs.fetch?.queries && isObject(inputs.fetch.queries)) {
    for (const [key, value] of Object.entries(inputs.fetch.queries)) {
      fetchQueryParamNames.push(key);
      const prefixedKey = `fetch.queries.${key}`;
      if (typeof value === 'string') {
        const parseResult = parseValueToSegments(value);
        if (parseResult.success && parseResult.segments.length > 0) {
          values[prefixedKey] = parseResult.segments;
        } else {
          values[prefixedKey] = value;
        }
      } else if (value !== undefined && value !== null) {
        values[prefixedKey] = value as ParameterValue;
      }
    }
  }
  values['_fetchQueryParamNames'] = fetchQueryParamNames;

  // Extract subscribe-only query parameters (those not in fetch)
  // Usually subscribe shares the same params, but check for differences
  const subscribeQueryParamNames: string[] = [];
  if (inputs.subscribe?.queries && isObject(inputs.subscribe.queries)) {
    for (const [key, value] of Object.entries(inputs.subscribe.queries)) {
      // Skip if already extracted from fetch
      if (fetchQueryParamNames.includes(key)) continue;
      subscribeQueryParamNames.push(key);
      const prefixedKey = `subscribe.queries.${key}`;
      if (typeof value === 'string') {
        const parseResult = parseValueToSegments(value);
        if (parseResult.success && parseResult.segments.length > 0) {
          values[prefixedKey] = parseResult.segments;
        } else {
          values[prefixedKey] = value;
        }
      } else if (value !== undefined && value !== null) {
        values[prefixedKey] = value as ParameterValue;
      }
    }
  }
  values['_subscribeQueryParamNames'] = subscribeQueryParamNames;

  return values;
}

/**
 * Build notification trigger inputs from parameter values.
 * Reconstructs the fetch/subscribe structure with query parameters.
 *
 * Parameters use prefixed keys from swagger:
 * - fetch.queries.{paramName} -> inputs.fetch.queries.{paramName}
 * - subscribe.queries.{paramName} -> inputs.subscribe.queries.{paramName}
 *
 * @param parameterValues - The parameter values from the editor (with prefixed keys)
 * @param originalInputs - The original inputs object
 * @returns The rebuilt inputs object
 */
export function buildNotificationTriggerInputs(
  parameterValues: Record<string, ParameterValue>,
  originalInputs: Record<string, unknown>
): Record<string, unknown> {
  // Start with a deep clone of original inputs to preserve structure
  const inputs = JSON.parse(JSON.stringify(originalInputs)) as Record<string, unknown>;

  // Update connection if changed
  if (parameterValues['_connection']) {
    if (!inputs.host) inputs.host = {};
    const host = inputs.host as Record<string, unknown>;
    if (!host.connection) host.connection = {};
    (host.connection as Record<string, unknown>).referenceName = parameterValues['_connection'] as string;
  }

  // Preserve host.apiId — critical for connector identity on notification/webhook triggers.
  // Without this, the trigger loses its connector association on save, causing the read chain
  // (useNodeOperationInfo) to fall through to the fragile heuristic path on next load.
  if (parameterValues['_apiId']) {
    if (!inputs.host) inputs.host = {};
    (inputs.host as Record<string, unknown>).apiId = parameterValues['_apiId'];
  }

  // Helper to serialize a parameter value
  const serializeValue = (value: ParameterValue): unknown => {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'type' in value[0]) {
      // Value segments - serialize them
      return serializeSegmentsToValue(value as ValueSegment[]);
    }
    return value;
  };

  // Build fetch.queries from prefixed parameter values
  const fetchQueries: Record<string, unknown> = {};
  const fetchQueryParamNames = (parameterValues['_fetchQueryParamNames'] as string[]) || [];
  for (const paramName of fetchQueryParamNames) {
    const prefixedKey = `fetch.queries.${paramName}`;
    const value = parameterValues[prefixedKey];
    if (!isEmptyParamValue(value)) {
      fetchQueries[paramName] = serializeValue(value);
    }
  }

  // Also check for parameters without stored names (new params added from swagger)
  for (const [key, value] of Object.entries(parameterValues)) {
    if (key.startsWith('fetch.queries.') && !isEmptyParamValue(value)) {
      const paramName = key.substring('fetch.queries.'.length);
      if (!fetchQueries[paramName]) {
        fetchQueries[paramName] = serializeValue(value);
      }
    }
  }

  // Update fetch.queries
  if (inputs.fetch && isObject(inputs.fetch)) {
    const fetch = inputs.fetch as Record<string, unknown>;
    if (Object.keys(fetchQueries).length > 0) {
      fetch.queries = fetchQueries;
    }
  }

  // Build subscribe.queries
  // Start with fetch queries (they share common params)
  const subscribeQueries: Record<string, unknown> = { ...fetchQueries };

  // Add subscribe-specific params
  const subscribeQueryParamNames = (parameterValues['_subscribeQueryParamNames'] as string[]) || [];
  for (const paramName of subscribeQueryParamNames) {
    const prefixedKey = `subscribe.queries.${paramName}`;
    const value = parameterValues[prefixedKey];
    if (!isEmptyParamValue(value)) {
      subscribeQueries[paramName] = serializeValue(value);
    }
  }

  // Also check for subscribe params without stored names
  for (const [key, value] of Object.entries(parameterValues)) {
    if (key.startsWith('subscribe.queries.') && !isEmptyParamValue(value)) {
      const paramName = key.substring('subscribe.queries.'.length);
      if (!subscribeQueries[paramName]) {
        subscribeQueries[paramName] = serializeValue(value);
      }
    }
  }

  // Update subscribe.queries
  if (inputs.subscribe && isObject(inputs.subscribe)) {
    const subscribe = inputs.subscribe as Record<string, unknown>;
    if (Object.keys(subscribeQueries).length > 0) {
      subscribe.queries = subscribeQueries;
    }
  }

  return inputs;
}

/**
 * Get the parameter location from a parameter definition.
 */
export function getParameterLocation(param: ParameterInfo): ParameterLocation | undefined {
  return param.info?.in;
}

/**
 * Group parameters by their location.
 */
export function groupParametersByLocation(
  params: ParameterInfo[]
): Record<ParameterLocation | 'other', ParameterInfo[]> {
  const result: Record<ParameterLocation | 'other', ParameterInfo[]> = {
    path: [],
    query: [],
    header: [],
    body: [],
    formData: [],
    other: [],
  };

  for (const param of params) {
    const location = param.info?.in;
    if (location && location in result) {
      result[location].push(param);
    } else {
      result.other.push(param);
    }
  }

  return result;
}

// =============================================================================
// COMBINED EXTRACTION (for use in extractParameterValues)
// =============================================================================

/**
 * Extract all swagger-based parameters from inputs.
 * This extracts queries, headers, body, and stores path/method as internal fields.
 *
 * For path parameters, we need the swagger path template to properly extract values.
 * Without it, we preserve the path as-is and let the UI handle path display.
 *
 * @param inputs - The inputs object from the action/trigger
 * @param pathTemplate - Optional swagger path template for path parameter extraction
 * @param parameters - Optional ParameterInfo array for metadata-driven extraction
 * @returns Record of parameter values
 */
export function extractSwaggerParameters(
  inputs: Record<string, unknown>,
  pathTemplate?: string,
  parameters?: ParameterInfo[]
): Record<string, ParameterValue> {
  const values: Record<string, ParameterValue> = {};
  const pathParamNames: string[] = [];
  const queryParamNames: string[] = [];
  const headerParamNames: string[] = [];

  // Store method for later serialization
  if ('method' in inputs) {
    values['_method'] = inputs.method as string;
  }

  // Store path for later serialization
  if ('path' in inputs) {
    values['_path'] = inputs.path as string;

    // If we have a path template, extract path parameters and store the template
    if (pathTemplate && typeof inputs.path === 'string') {
      // Normalize: saved `inputs.path` never carries `/{connectionId}`, so a
      // prefixed template would mismatch segment counts and silently drop
      // every path param (fuzzy-matcher anchor fails on the leading literal).
      const normalizedTemplate = stripConnectionIdPrefix(pathTemplate);
      values['_swaggerPathTemplate'] = normalizedTemplate;

      // Use metadata-driven extraction when parameters are provided
      const pathParams = parameters
        ? extractPathParametersWithMetadata(inputs.path, normalizedTemplate, parameters)
        : extractPathParameters(inputs.path, normalizedTemplate);

      for (const [name, value] of Object.entries(pathParams)) {
        pathParamNames.push(name);
        // Parse expressions in path parameter values
        if (typeof value === 'string' && (value.includes('@') || value.includes('${'))) {
          const parseResult = parseValueToSegments(value);
          if (parseResult.success && parseResult.segments.length > 0) {
            values[name] = parseResult.segments;
          } else {
            values[name] = value;
          }
        } else {
          values[name] = value;
        }
      }
    } else if (typeof inputs.path === 'string' && (inputs.path.includes('@{encodeURIComponent(') || inputs.path.includes('${encodeURIComponent('))) {
      // No path template available, but path contains encoded parameters
      // NOTE: This fallback pattern is UNRELIABLE - it assumes URL segment before expression is parameter name
      // e.g., /teams/@{encodeURIComponent('id')} would incorrectly extract "teams" as parameter name
      // The correct parameter name (e.g., "groupId") is in the swagger path template
      const pathParamPattern = /\/([a-zA-Z_][a-zA-Z0-9_]*)\/[@$]\{encodeURIComponent\('([^']+)'\)\}/g;
      let match;
      while ((match = pathParamPattern.exec(inputs.path)) !== null) {
        const [, paramName, paramValue] = match;
        if (paramName && paramValue) {
          pathParamNames.push(paramName);
          values[paramName] = paramValue;
        }
      }
    }
  }

  // Handle pathTemplate format (alternative to path)
  if ('pathTemplate' in inputs && typeof inputs.pathTemplate === 'object') {
    const pathTemplateObj = inputs.pathTemplate as { template?: string; parameters?: Record<string, unknown> };
    values['_pathTemplate'] = pathTemplateObj.template || '';
    if (pathTemplateObj.parameters) {
      for (const [name, value] of Object.entries(pathTemplateObj.parameters)) {
        pathParamNames.push(name);
        if (typeof value === 'string' && (value.includes('@') || value.includes('${'))) {
          const parseResult = parseValueToSegments(value);
          if (parseResult.success && parseResult.segments.length > 0) {
            values[name] = parseResult.segments;
          } else {
            values[name] = value;
          }
        } else {
          values[name] = value as ParameterValue;
        }
      }
    }
  }

  // Extract query parameters
  if ('queries' in inputs && isObject(inputs.queries)) {
    const queryParams = extractQueryParameters(inputs.queries);
    queryParamNames.push(...Object.keys(queryParams));
    Object.assign(values, queryParams);
  }

  // Extract header parameters
  if ('headers' in inputs && isObject(inputs.headers)) {
    const headerParams = extractHeaderParameters(inputs.headers);
    // Prefix header params to avoid conflicts
    for (const [name, value] of Object.entries(headerParams)) {
      headerParamNames.push(name);
      values[`header.${name}`] = value;
    }
  }

  // Extract body parameters
  if ('body' in inputs) {
    const bodyParams = extractBodyParameters(inputs.body);
    Object.assign(values, bodyParams);

    // For body-root params (the whole `inputs.body` is the field value), mirror
    // the value to the named field key so the editor renders it on mount.
    // Primitive bodies are stored under the literal `body` key by
    // `extractBodyParameters`; object/dictionary bodies are flattened so we
    // fall back to the raw `inputs.body` for those.
    if (parameters) {
      const bodyRoot = findRootBodyParam(parameters.filter((p) => p.info?.in === 'body'));
      const rootKey = bodyRoot && (bodyRoot.parameterKey || bodyRoot.parameterName);
      if (rootKey && rootKey !== 'body' && !(rootKey in values)) {
        values[rootKey] = (bodyParams.body !== undefined ? bodyParams.body : inputs.body) as ParameterValue;
      }
    }
  }

  // Store parameter location metadata for serialization
  if (pathParamNames.length > 0) {
    values['_pathParamNames'] = pathParamNames;
  }
  if (queryParamNames.length > 0) {
    values['_queryParamNames'] = queryParamNames;
  }
  if (headerParamNames.length > 0) {
    values['_headerParamNames'] = headerParamNames;
  }

  return values;
}

// =============================================================================
// COMBINED SERIALIZATION (for use in buildInputsFromParameters)
// =============================================================================

/**
 * Build swagger-based inputs from parameter values.
 *
 * @param parameterValues - The parameter values from the editor
 * @param pathTemplate - The swagger path template for path parameter serialization
 * @param parameterDefs - Parameter definitions to determine locations
 * @returns The reconstructed inputs object
 */
export function buildSwaggerInputs(
  parameterValues: Record<string, ParameterValue>,
  pathTemplate: string | undefined,
  parameterDefs: ParameterInfo[]
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  const grouped = groupParametersByLocation(parameterDefs);

  // Get method from internal field
  if ('_method' in parameterValues) {
    inputs.method = parameterValues['_method'];
  }

  // Handle path parameters
  if (pathTemplate && grouped.path.length > 0) {
    const pathParamNames = grouped.path.map(p => p.parameterName);
    const pathParams: Record<string, string> = {};
    for (const name of pathParamNames) {
      if (name in parameterValues) {
        const value = parameterValues[name];
        pathParams[name] = typeof value === 'string' ? value : String(value);
      }
    }
    inputs.path = serializePathParameters(pathParams, pathTemplate);
  } else if ('_path' in parameterValues) {
    // Preserve original path if no template available
    inputs.path = parameterValues['_path'];
  }

  // Handle query parameters
  if (grouped.query.length > 0) {
    const queryParamNames = grouped.query.map(p => p.parameterName);
    const queries = serializeQueryParameters(
      parameterValues as Record<string, unknown>,
      queryParamNames
    );
    if (Object.keys(queries).length > 0) {
      inputs.queries = queries;
    }
  }

  // Handle header parameters
  if (grouped.header.length > 0) {
    const headers = serializeHeaderParameters(
      parameterValues as Record<string, unknown>,
      grouped.header.map((p) => p.parameterName),
    );
    if (Object.keys(headers).length > 0) {
      inputs.headers = headers;
    }
  }

  // Handle body parameters
  const body = serializeBodyParameters(parameterValues as Record<string, unknown>);
  if (body !== undefined) {
    inputs.body = body;
  }

  return inputs;
}

/**
 * Serialize swagger parameters back to inputs using stored metadata.
 *
 * This function uses the location metadata stored during extraction
 * (_pathParamNames, _queryParamNames, _headerParamNames, _swaggerPathTemplate)
 * to reconstruct the inputs object without needing external parameter definitions.
 *
 * @param parameterValues - The parameter values from the editor (includes metadata)
 * @returns The reconstructed inputs object, or null if not a swagger operation
 */
export function serializeSwaggerParameters(
  parameterValues: Record<string, ParameterValue>
): Record<string, unknown> | null {
  // Check if this was extracted as a swagger operation
  const hasSwaggerMetadata =
    '_method' in parameterValues ||
    '_path' in parameterValues ||
    '_pathTemplate' in parameterValues ||
    '_queryParamNames' in parameterValues;

  if (!hasSwaggerMetadata) {
    return null;
  }

  const inputs: Record<string, unknown> = {};

  // Restore method
  if ('_method' in parameterValues) {
    inputs.method = parameterValues['_method'];
  }

  // Get stored metadata arrays
  const pathParamNames = (parameterValues['_pathParamNames'] as string[] | undefined) ?? [];
  const queryParamNames = (parameterValues['_queryParamNames'] as string[] | undefined) ?? [];
  const headerParamNames = (parameterValues['_headerParamNames'] as string[] | undefined) ?? [];
  const swaggerPathTemplate = parameterValues['_swaggerPathTemplate'] as string | undefined;

  // Serialize path parameters
  if (swaggerPathTemplate && pathParamNames.length > 0) {
    // Rebuild path from template and parameter values
    const pathParams: Record<string, string> = {};
    for (const name of pathParamNames) {
      if (name in parameterValues) {
        const value = parameterValues[name];
        pathParams[name] = typeof value === 'string' ? value : String(value ?? '');
      }
    }
    inputs.path = serializePathParameters(pathParams, swaggerPathTemplate);
  } else if ('_path' in parameterValues) {
    // No template available - preserve original path
    inputs.path = parameterValues['_path'];
  }

  // Handle pathTemplate format (alternative structure)
  if ('_pathTemplate' in parameterValues && parameterValues['_pathTemplate']) {
    const template = parameterValues['_pathTemplate'] as string;
    const parameters: Record<string, unknown> = {};
    for (const name of pathParamNames) {
      if (name in parameterValues) {
        parameters[name] = parameterValues[name];
      }
    }
    inputs.pathTemplate = {
      template,
      parameters,
    };
    // Remove the path key if using pathTemplate format
    delete inputs.path;
  }

  // Serialize query parameters
  if (queryParamNames.length > 0) {
    const queries: Record<string, unknown> = {};
    for (const name of queryParamNames) {
      if (name in parameterValues && parameterValues[name] !== undefined && parameterValues[name] !== '') {
        queries[name] = parameterValues[name];
      }
    }
    if (Object.keys(queries).length > 0) {
      inputs.queries = queries;
    }
  }

  // Serialize header parameters
  if (headerParamNames.length > 0) {
    const headers = serializeHeaderParameters(
      parameterValues as Record<string, unknown>,
      headerParamNames,
    );
    if (Object.keys(headers).length > 0) {
      inputs.headers = headers;
    }
  }

  // Serialize body parameters
  const body = serializeBodyParameters(parameterValues as Record<string, unknown>);
  if (body !== undefined) {
    inputs.body = body;
  }

  return inputs;
}

// =============================================================================
// DEFINITION-BASED SWAGGER SERIALIZATION
// =============================================================================

/**
 * Connection reference key format - determines host structure
 * Following LogicAppsUX ConnectionReferenceKeyFormat enum
 */
export type ConnectionReferenceKeyFormat =
  | 'ApiConnection'         // Standard: host.connection.referenceName only
  | 'ApiConnectionWebhook'  // Webhook: same flat shape as ApiConnection
  | 'ServiceProvider';      // Service Provider: serviceProviderConfiguration

/**
 * Merge swagger-derived parameters with dynamic-schema-resolved children,
 * dropping the swagger placeholder(s) that the dynamic schema expanded.
 *
 * Resolved children carry `info.generatedByParameter` set to the parent's
 * `parameterKey` (e.g. SQL ExecuteProcedure_V2: 'parameters', SharePoint
 * PostItem: 'body'). We drop any swagger param whose `parameterKey` matches
 * one of those parent keys so the saved payload doesn't contain both the
 * placeholder body param and its expansion.
 *
 * Order is preserved: filtered swagger params come first (so path params keep
 * their canonical ordering), then dynamic schema params.
 */
export function mergeSwaggerAndDynamicSchemaParams(
  swaggerParams: ParameterInfo[],
  dynamicSchemaParams: ParameterInfo[],
): ParameterInfo[] {
  const replacedKeys = new Set<string>();
  for (const p of dynamicSchemaParams) {
    if (p.info?.generatedByParameter) {
      replacedKeys.add(p.info.generatedByParameter);
    }
  }
  const filteredSwagger = replacedKeys.size > 0
    ? swaggerParams.filter(sp => !replacedKeys.has(sp.parameterKey))
    : swaggerParams;
  return [...filteredSwagger, ...dynamicSchemaParams];
}

/**
 * Build swagger-based inputs from parameter values using parameter definitions.
 *
 * This function is used when we have swagger parameter definitions available
 * (from useOperationManifestData). It properly routes parameters to their
 * correct locations based on `info.in`:
 * - path params → substituted into the path URL
 * - query params → inputs.queries object
 * - header params → inputs.headers object
 * - body params → inputs.body
 *
 * The resulting structure matches what LogicAppsUX produces and what the
 * Azure Logic Apps runtime expects.
 *
 * @param parameterValues - The parameter values from the editor
 * @param parameterDefs - Parameter definitions with info.in locations
 * @param method - HTTP method (get, post, put, delete, etc.)
 * @param pathTemplate - Swagger path template with {paramName} placeholders
 * @param defaultPathEncoding - Default encoding level for path params (1=single, 2=double encodeURIComponent)
 * @param connectionFormat - Connection reference format (ApiConnection, ServiceProvider)
 * @param connectorId - Connector ID (used to populate host.apiId)
 * @param operationId - Operation ID (used to populate host.operationId)
 * @param originalInputs - Original inputs to preserve path/method when template is empty (dynamic schema case)
 * @returns The complete inputs object matching LogicAppsUX/runtime format
 */
export function buildSwaggerInputsFromDefinitions(
  parameterValues: Record<string, ParameterValue>,
  parameterDefs: ParameterInfo[],
  method: string,
  pathTemplate: string,
  defaultPathEncoding: number = 2,
  connectionFormat: ConnectionReferenceKeyFormat = 'ApiConnection',
  connectorId?: string,
  operationId?: string,
  originalInputs?: Record<string, unknown>
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  const grouped = groupParametersByLocation(parameterDefs);

  // 1. Set HTTP method
  // Priority: explicit method param > extracted _method from parameterValues > originalInputs.method
  // ApiConnectionWebhook omits `method` — the runtime always POSTs to the subscribe
  // path and reads the HTTP verb from the connector's x-ms-notification-content swagger,
  // so emitting it on the action would be redundant and inconsistent with the
  // construction-time shape (see workflowHelpers.ts ApiConnectionWebhook block).
  if (connectionFormat !== 'ApiConnectionWebhook') {
    if (method) {
      inputs.method = method.toLowerCase();
    } else if (parameterValues['_method']) {
      // Use extracted method from when node was loaded (for existing actions)
      inputs.method = (parameterValues['_method'] as string).toLowerCase();
    } else if (originalInputs?.method) {
      inputs.method = originalInputs.method;
    }
  }

  // 2. Build host based on connection format using centralized serializer
  // For ApiConnection types, the host block must ALWAYS be present — the runtime needs
  // apiId + operationId to route the API call, even before a connection is configured.
  // When no connection exists yet, referenceName is empty string "".
  const connectionRef = parameterValues['_connection'] as string | undefined;
  const isApiConnectionType =
    connectionFormat === 'ApiConnection' ||
    connectionFormat === 'ApiConnectionWebhook';

  if (connectionRef || isApiConnectionType) {
    const apiId = connectorId;
    // Also check _apiId/_operationId from parameter values (preserved from original definition during read)
    const resolvedApiId = apiId ?? (parameterValues['_apiId'] as string | undefined);
    const resolvedOperationId = operationId ?? (parameterValues['_operationId'] as string | undefined);
    const serialized = serializeHost({
      connectionType: connectionFormat ?? 'ApiConnection',
      connectionKey: connectionRef || '',
      ...(resolvedApiId ? { apiId: resolvedApiId } : {}),
      ...(resolvedOperationId ? { operationId: resolvedOperationId } : {}),
    });
    applyHostSerialization(inputs, serialized);
  }

  // 3. Build path by substituting path parameters into the template
  // IMPORTANT: Parse placeholders from the template itself, not just from grouped.path
  // This handles dynamic parameters (from x-ms-dynamic-schema) that may not have info.in set
  if (pathTemplate) {
    let path = stripConnectionIdPrefix(pathTemplate);

    // Find ALL path parameter placeholders in the template using regex
    // This substitutes any dynamic path params even if they're not in grouped.path
    const placeholderRegex = /\{([^}]+)\}/g;
    let match: RegExpExecArray | null;
    const placeholders: string[] = [];
    while ((match = placeholderRegex.exec(path)) !== null) {
      if (match[1]) {
        placeholders.push(match[1]);
      }
    }

    // Substitute each placeholder found in the path template
    for (const paramName of placeholders) {
      const value = parameterValues[paramName];

      if (!isEmptyParamValue(value)) {
        // Get the serialized string value
        const stringValue = serializeParameterValueToString(value);

        // Find the parameter definition (if exists) to get encoding level
        // Try both grouped.path and all parameterDefs for dynamic params
        const paramDef = grouped.path.find(p =>
          p.parameterName.toLowerCase() === paramName.toLowerCase()
        ) ?? parameterDefs.find(p =>
          p.parameterName.toLowerCase() === paramName.toLowerCase()
        );

        // Get encoding level from parameter's info.encode, or use default
        // LogicAppsUX uses 'double' -> 2, anything else -> 1
        const encodeLevel = getEncodeLevel(paramDef?.info?.encode, defaultPathEncoding);

        // Encode the value for URL path
        // LogicAppsUX uses encodePathValueWithFunction which wraps in @{encodeURIComponent(...)}
        const encodedValue = encodePathParameterValue(stringValue, encodeLevel);

        // Replace the placeholder (case-insensitive)
        const placeholder = new RegExp(`\\{${paramName}\\}`, 'gi');
        path = path.replace(placeholder, encodedValue);
      }
    }

    inputs.path = path;
  } else if (parameterValues['_path']) {
    // Use extracted path from when node was loaded (for existing actions with dynamic schema)
    // The extracted path already has parameter values substituted correctly
    inputs.path = parameterValues['_path'] as string;
  } else if (originalInputs?.path) {
    // Fallback to originalInputs.path
    inputs.path = originalInputs.path;
  }

  // 4. Build queries object from query parameters
  // Use user-provided value if set, otherwise fall back to swagger default value
  const queries: Record<string, unknown> = {};
  for (const param of grouped.query) {
    const paramName = param.parameterName;
    const value = parameterValues[paramName];

    if (!isEmptyParamValue(value)) {
      // Serialize the value, JSON stringifying complex types (arrays/objects)
      queries[paramName] = serializeQueryParameterValue(value);
    } else if (param.defaultValue !== undefined) {
      // Use default value from swagger if no user value is set
      queries[paramName] = param.defaultValue;
    }
  }
  if (Object.keys(queries).length > 0) {
    inputs.queries = queries;
  }

  // 5. Build headers object from header parameters
  const headers: Record<string, unknown> = {};
  for (const param of grouped.header) {
    const paramName = param.parameterName;
    // Bare key (user edits via `setParameter`) wins over the extracted
    // prefixed key so edits — including clears — aren't shadowed.
    const value = paramName in parameterValues
      ? parameterValues[paramName]
      : parameterValues[`header.${paramName}`];

    if (!isEmptyParamValue(value)) {
      headers[paramName] = serializeParameterValueToString(value);
    } else if (param.defaultValue !== undefined) {
      // Fall back to swagger default (parity with the queries branch above).
      headers[paramName] = param.defaultValue;
    }
  }
  if (Object.keys(headers).length > 0) {
    inputs.headers = headers;
  }

  // 6. Build body from body parameters.
  // When body definitions exist, they are authoritative: each `parameterKey`
  // already encodes the literal path inside `inputs.body` (e.g. `To`,
  // `Recipient.Name`, or `body.content` for connectors whose schema wraps
  // fields under an outer property also named `body`, such as Teams
  // `PostMessageToSelf`). Stripping a leading `body.` here would collapse
  // that wrapper and break the runtime payload. Falls back to the generic
  // `serializeBodyParameters` only when no body defs are provided.
  let finalBody: unknown;

  if (grouped.body.length > 0) {
    // A single body param represents the whole body (parity with LogicAppsUX
    // `constructInputValues`, which returns the `body.$` root parameter's
    // value directly rather than wrapping it as `{ [paramName]: value }`).
    const onlyBodyParam = findRootBodyParam(grouped.body);

    if (onlyBodyParam) {
      const rootKey = onlyBodyParam.parameterKey || onlyBodyParam.parameterName;
      const value = parameterValues[rootKey] ?? parameterValues['body'];
      if (!isEmptyParamValue(value)) {
        finalBody = serializeBodyValue(value, onlyBodyParam);
      } else if (onlyBodyParam.defaultValue !== undefined) {
        finalBody = onlyBodyParam.defaultValue;
      }
    } else {
      const bodyFromDefs: Record<string, unknown> = {};
      let hasBodyFromDefs = false;
      // Mirrors LogicAppsUX: the x-ms-dynamic-schema parent placeholder is
      // never serialized — resolved leaf children carry the real keys via
      // their own `parameterKey` (e.g. `parameters.Id`). If no children
      // produced values (empty proc / unresolved schema), still emit
      // `body: {}` so connectors like SQL ExecuteProcedure_V2 receive a
      // valid empty payload rather than a missing body.
      let hadDynamicSchemaBody = false;

      for (const param of grouped.body) {
        if (param.info?.dynamicSchema) {
          hadDynamicSchemaBody = true;
          continue;
        }

        const paramKey = param.parameterKey || param.parameterName;
        // Dynamic schema params may store values with or without the `body.` prefix.
        const value = parameterValues[paramKey]
          ?? parameterValues[param.parameterName]
          ?? parameterValues[`body.${param.parameterName}`]
          ?? parameterValues[`body.${paramKey}`];

        let resolvedValue: unknown;
        if (!isEmptyParamValue(value)) {
          resolvedValue = serializeBodyValue(value, param);
        } else if (param.defaultValue !== undefined) {
          // Match the query-parameter behavior: fall back to the swagger default
          // so required hidden fields (e.g. Teams `contentType` default "text")
          // still reach the runtime when the user hasn't touched them.
          resolvedValue = param.defaultValue;
        } else {
          continue;
        }

        if (paramKey.includes('.')) {
          setNestedBodyValue(bodyFromDefs, paramKey, resolvedValue);
        } else {
          bodyFromDefs[paramKey] = resolvedValue;
        }
        hasBodyFromDefs = true;
      }

      if (hasBodyFromDefs) {
        finalBody = bodyFromDefs;
      } else if (hadDynamicSchemaBody) {
        // Empty-schema fallback: emit `body: {}` so the connector receives an
        // explicit empty payload (matches the working SQL ExecuteProcedure_V2
        // reference payload for procedures with no parameters).
        finalBody = {};
      }
    }
  } else {
    finalBody = serializeBodyParameters(parameterValues as Record<string, unknown>);
  }

  if (finalBody !== undefined) {
    inputs.body = finalBody;
  }

  // 7. Handle "other" parameters (no location specified) - includes invisible/builtin params
  // These go at the top level of inputs (like inferContentType, queryParametersSingleEncoded)
  // Following LogicAppsUX serializeInvisibleData pattern
  for (const param of grouped.other) {
    const paramName = param.parameterName;
    const value = parameterValues[paramName];

    // Skip internal fields and already-handled fields
    if (paramName.startsWith('_') || paramName === 'host' || paramName === 'method') {
      continue;
    }

    // Include the parameter if it has a value OR if it's hidden with a default value
    // Hidden parameters (hideInUI) often contain required defaults that must be serialized
    if (value !== undefined && value !== null) {
      inputs[paramName] = serializeParameterValueToString(value);
    } else if (param.hideInUI && param.value !== undefined && param.value !== null) {
      // Serialize hidden parameter's default value
      inputs[paramName] = param.value;
    }
  }

  // 8. Serialize invisible/hidden parameters from ALL location groups
  // LogicAppsUX's serializeInvisibleData includes hidden params regardless of location
  serializeHiddenParameters(parameterDefs, parameterValues, inputs);

  return inputs;
}

/**
 * Get the encoding level from info.encode metadata.
 *
 * Encoding levels from x-ms-url-encoding swagger extension:
 * - 'none' or '' → 0 (literal, no encoding)
 * - 'single' → 1 (single encodeURIComponent)
 * - 'double' → 2 (double encodeURIComponent)
 * - undefined → defaultLevel (typically 1 for single encoding)
 *
 * Following LogicAppsUX pattern for URL encoding.
 */
function getEncodeLevel(encode: string | undefined, defaultLevel: number): number {
  if (!encode) return defaultLevel;
  const lower = encode.toLowerCase();
  if (lower === 'double') return 2;
  if (lower === 'none' || lower === '' || lower === '0') return 0;
  return 1; // 'single' or any other value
}

/**
 * Set a nested value in a body object using dot notation path.
 * e.g., setNestedBodyValue(obj, "recipient.groupId", value) sets obj.recipient.groupId = value
 */
function setNestedBodyValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (!(part in current) || typeof current[part] !== 'object' || current[part] === null) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  const lastPart = parts[parts.length - 1]!;
  current[lastPart] = value;
}

/**
 * Identify the single body parameter (if any) that represents the whole
 * request body — either by the bare `body` parameterKey or the
 * `isBodyRoot` flag set by SwaggerParser for primitive/dictionary schemas
 * with no `properties` to flatten. Mirrors LogicAppsUX `body.$` root-parameter
 * detection in `constructInputValues`.
 */
function findRootBodyParam(bodyParams: ParameterInfo[]): ParameterInfo | undefined {
  if (bodyParams.length !== 1) return undefined;
  const only = bodyParams[0]!;
  if (only.info?.isBodyRoot) return only;
  const key = only.parameterKey || only.parameterName;
  return key === 'body' ? only : undefined;
}

/**
 * Serialize query parameter value, JSON stringifying complex types.
 * LogicAppsUX JSON stringifies arrays and objects in query parameters.
 */
function serializeQueryParameterValue(value: ParameterValue): unknown {
  // If it's a ValueSegment array, serialize to string
  if (Array.isArray(value) && value.length > 0 && value[0]?.id && value[0]?.type) {
    return serializeSegmentsToValue(value);
  }
  // JSON stringify arrays and objects
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    return JSON.stringify(value);
  }
  // Primitives pass through
  return value;
}

/**
 * Serialize hidden/invisible parameters to inputs.
 * Following LogicAppsUX serializeInvisibleData pattern - hidden parameters
 * with hideInUI: true must be included in the serialized output with their
 * default values, as they often contain required internal configuration.
 */
function serializeHiddenParameters(
  parameterDefs: ParameterInfo[],
  parameterValues: Record<string, ParameterValue>,
  inputs: Record<string, unknown>
): void {
  for (const param of parameterDefs) {
    // Only process hidden parameters
    if (!param.hideInUI) continue;

    const paramName = param.parameterName;

    // Skip if already handled (has a value in parameterValues or already in inputs)
    if (parameterValues[paramName] !== undefined) continue;
    if (inputs[paramName] !== undefined) continue;

    // Skip internal location params that go to specific objects (path, query, header, body)
    // These should be handled by their respective serializers
    if (param.info?.in === 'path' || param.info?.in === 'query' ||
        param.info?.in === 'header' || param.info?.in === 'body') {
      continue;
    }

    // Include the hidden parameter's default value if it exists
    if (param.value !== undefined && param.value !== null) {
      inputs[paramName] = param.value;
    }
  }
}

/**
 * Serialize a parameter value to a string.
 * Handles ValueSegment arrays by converting them to expression strings.
 */
function serializeParameterValueToString(value: ParameterValue): unknown {
  // If it's a ValueSegment array, serialize to string
  if (Array.isArray(value) && value.length > 0 && value[0]?.id && value[0]?.type) {
    return serializeSegmentsToValue(value);
  }
  // If it's already a primitive, return as-is
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  // For objects/arrays, return as-is (they'll be serialized by the workflow serializer)
  return value;
}

// Body values get type-aware coercion: when a body field's schema is `object`
// or `array`, a JSON-shaped string is parsed back into a native value. Catches
// values authored before body-type detection was fixed in SwaggerParser, so
// e.g. `'{"k":"v"}'` stored for a `type: object` field doesn't round-trip as
// a literal string. Strings on string-typed fields pass through untouched.
// Callers must guard with `isEmptyParamValue` first — null/undefined are not
// allowed by the shared serializer's signature.
function serializeBodyValue(value: ParameterValue, param: ParameterInfo): unknown {
  return serializeParameterValue(
    value as Exclude<ParameterValue, null>,
    param.schema?.type ?? param.type,
  );
}

/**
 * Check if a value is an expression that should not be double-wrapped.
 * Handles multiple expression syntaxes:
 * - ${...} - JavaScript expression syntax (Otto)
 * - @{...} - legacy workflow expression syntax
 * - @expression - direct expression reference
 * - Multi-segment expressions like "prefix ${...} suffix"
 */
function isExpressionValue(value: string): boolean {
  if (!value) return false;
  // Check for @{...} expression syntax
  if (value.includes('@{')) return true;
  // Check for ${...} expression syntax
  if (value.includes('${')) return true;
  // Check for @expression syntax (followed by function call or identifier)
  if (/^@[a-zA-Z]/.test(value)) return true;
  return false;
}

/**
 * Encode a path parameter value for URL substitution.
 *
 * Encoding levels:
 * - 0: Literal value (no wrapper, no encoding)
 * - 1: Single encoding: ${encodeURIComponent('value')}
 * - 2: Double encoding: ${encodeURIComponent(encodeURIComponent('value'))}
 *
 * @param value - The string value to encode
 * @param encodeCount - Number of times to wrap in encodeURIComponent (0, 1, or 2)
 * @returns The encoded expression string or literal value
 */
function encodePathParameterValue(value: unknown, encodeCount: number): string {
  // If value is already an expression, return as-is - it's already in the correct format
  // Using proper expression detection instead of simple string match
  if (typeof value === 'string' && isExpressionValue(value)) {
    return value;
  }

  const stringValue = typeof value === 'string' ? value : String(value);

  // encodeCount = 0 means literal value (no @{...} wrapper)
  // This is used when the swagger specifies no encoding for the parameter
  if (encodeCount === 0) {
    return stringValue;
  }

  // For non-zero encodeCount, wrap in encodeURIComponent expression
  // Escape single quotes in the value (double them for Logic Apps expression syntax)
  const escapedValue = stringValue.replace(/'/g, "''");
  let encoded = `'${escapedValue}'`;

  for (let i = 0; i < encodeCount; i++) {
    encoded = `encodeURIComponent(${encoded})`;
  }

  return `\${${encoded}}`;
}
